import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { RaptorLogo } from '@/components/RaptorLogo';
import { SurveyForm, SchemaShape, isVisible as isQuestionVisible } from '@/components/SurveyForm';
import { computeGeoAutofill } from '@/lib/geoautofill';
import { GeofenceGate, GeofenceCapture, GeofenceBypass, SiteGeofenceConfig } from '@/components/GeofenceGate';
import { SignaturePad, SignaturePadHandle } from '@/components/SignaturePad';
import { PhotoGallery } from '@/components/PhotoGallery';
import { toast } from 'sonner';
import { CheckCircle2, PenLine, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';

interface Resolved {
  instruction_id: string; template_id: string; template_version: number;
  template_name: string; client_name: string; client_address: string;
  site_reference: string; method: string; status: string;
  geofence_required: boolean;
  geo_mode?: 'strict' | 'capture_only';
  site_geofence?: SiteGeofenceConfig | null;
  raptor_mark_url?: string;
  schema: SchemaShape;
}

export const SurveyFillPage: React.FC = () => {
  const { token } = useParams();
  const nav = useNavigate();
  const [resolved, setResolved] = useState<Resolved | null>(null);
  const [step, setStep] = useState<'geofence' | 'form' | 'review' | 'done' | 'error'>('geofence');
  const [sectionIdx, setSectionIdx] = useState(0);
  const [errMsg, setErrMsg] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [geofence, setGeofence] = useState<GeofenceCapture | null>(null);
  const [geofenceBypass, setGeofenceBypass] = useState<GeofenceBypass | null>(null);
  const [lockedCodes, setLockedCodes] = useState<string[]>([]);
  const [clientNameInput, setClientNameInput] = useState('');
  const [busy, setBusy] = useState(false);
  // Camera-only surveys (the 13 desktop forms) capture photos per-question,
  // so hide the upload gallery entirely — no file uploads allowed.
  const cameraOnlySurvey = useMemo(() => {
    const secs = (resolved?.schema as { sections?: Array<{ questions?: Array<{ type: string; config?: { camera_only?: boolean } }> }> } | undefined)?.sections || [];
    return secs.some((s) => (s.questions || []).some((q) => q.type === 'photo_ref' && q.config?.camera_only));
  }, [resolved]);
  // Per-section completeness — drives the tappable progress segments and the
  // submit-time "what's missing" message. Free navigation; required enforced at submit.
  const sectionStatus = useMemo(() => {
    const secs = resolved?.schema.sections ?? [];
    return secs.map((sec) => {
      const missing: string[] = [];
      for (const q of sec.questions) {
        if (q.type === 'signature_ref') continue;
        if (!isQuestionVisible(q, answers)) continue;
        if (q.required && !answers[q.question_code]) missing.push(`${q.display_number ? q.display_number + ' ' : ''}${q.text}`);
      }
      return { complete: missing.length === 0, missing };
    });
  }, [resolved, answers]);

  const reviewIncomplete = sectionStatus
    .map((st, i) => ({ i, title: resolved?.schema.sections[i]?.title ?? `Section ${i + 1}`, missing: st.missing }))
    .filter((x) => x.missing.length > 0);
  const reviewMissingCount = reviewIncomplete.reduce((n, x) => n + x.missing.length, 0);

  // Template-driven signature blocks. When a template was never configured we
  // fall back to the legacy Technician + Client pads (zero disruption). When
  // configured, we use its slots exactly — an empty list means no signing.
  type SigSlot = { role: string; label: string; require_name?: boolean; required?: boolean; selfie_mode?: 'off' | 'optional' | 'required' };
  const signatureSlots = useMemo<SigSlot[]>(() => {
    const sc = resolved?.schema as unknown as { signatures_configured?: boolean; signature_slots?: SigSlot[] } | undefined;
    if (sc?.signatures_configured) return sc.signature_slots ?? [];
    return [
      { role: 'technician', label: 'Technician / Surveyor', require_name: true, required: false },
      { role: 'client', label: 'Client', require_name: true, required: false },
    ];
  }, [resolved]);
  const slotNameValue = (i: number, slot: SigSlot): string =>
    sigNames[i] ?? (/client/i.test(slot.role) ? (resolved?.client_name ?? '') : '');

  const sigRefs = useRef<Record<number, SignaturePadHandle | null>>({});
  const [sigNames, setSigNames] = useState<Record<number, string>>({});
  const [sigSelfies, setSigSelfies] = useState<Record<number, string>>({});
  const [photoIds, setPhotoIds] = useState<string[]>([]);

  const onSelfiePick = (i: number, file: File | undefined): void => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setSigSelfies((m) => ({ ...m, [i]: String(reader.result) }));
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (!token) return;
    axios.get<Resolved>(`${API_BASE}/public/surveys/${token}`)
      .then(r => { setResolved(r.data); setClientNameInput(r.data.client_name); })
      .catch((e) => { setStep('error'); setErrMsg(e?.response?.data?.detail ?? 'Invalid link'); });
  }, [token]);

  const onGeofenced = (g: GeofenceCapture, bypass?: GeofenceBypass) => {
    setGeofence(g);
    if (bypass) setGeofenceBypass(bypass);
    // Auto-fill location questions (latitude/longitude/GPS/address) from the
    // captured position; these fields render read-only.
    if (resolved?.schema) {
      const { values, locked } = computeGeoAutofill(resolved.schema, g.lat, g.lng);
      if (Object.keys(values).length) setAnswers((a) => ({ ...a, ...values }));
      setLockedCodes(locked);
    }
    setTimeout(() => setStep('form'), 600);
  };

  const validate = () => {
    if (!resolved) return false;
    const errs: Record<string, string> = {};
    for (const s of resolved.schema.sections) {
      for (const q of s.questions) {
        // Signatures are captured via the pads below, not as answers.
        if (q.type === 'signature_ref') continue;
        // Skip questions hidden by conditional (if-yes) branching.
        if (!isQuestionVisible(q, answers)) continue;
        if (q.required && !answers[q.question_code]) errs[q.question_code] = 'Required';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Validate only the required, visible questions in one section (wizard step).
  const jumpTo = (idx: number): void => { setErrors({}); setStep('form'); setSectionIdx(idx); scrollTop(); };

  const scrollTop = (): void => window.scrollTo({ top: 0, behavior: 'smooth' });

  const goNext = (): void => {
    if (!resolved) return;
    setErrors({});
    if (sectionIdx < resolved.schema.sections.length - 1) {
      setSectionIdx((i) => i + 1);
    } else {
      setStep('review');
    }
    scrollTop();
  };

  const goBack = (): void => {
    setErrors({});
    if (step === 'review') { setStep('form'); scrollTop(); return; }
    if (sectionIdx > 0) { setSectionIdx((i) => i - 1); scrollTop(); }
  };

  const submit = async () => {
    if (!token || !resolved) return;
    const incomplete = sectionStatus
      .map((st, i) => ({ i, title: resolved.schema.sections[i]?.title ?? `Section ${i + 1}`, missing: st.missing }))
      .filter((x) => x.missing.length > 0);
    if (incomplete.length > 0) {
      validate();
      const lines = incomplete
        .map((x) => `${x.title}: ${x.missing.slice(0, 4).join(', ')}${x.missing.length > 4 ? ` +${x.missing.length - 4} more` : ''}`)
        .join('  |  ');
      toast.error(`Missing required answers — ${lines}`);
      setStep('form'); setSectionIdx(incomplete[0].i); scrollTop();
      return;
    }
    setBusy(true);
    try {
      // Capture signatures first — one pad per configured block.
      const sigIds: string[] = [];
      const client = axios.create({ baseURL: API_BASE });
      let surveyorNameOut = '';
      let clientNameOut = clientNameInput || resolved.client_name;
      for (let i = 0; i < signatureSlots.length; i++) {
        const slot = signatureSlots[i];
        const ref = sigRefs.current[i];
        const nm = slotNameValue(i, slot).trim();
        const url = ref?.getDataUrl();
        const hasSig = !!url && !ref?.isEmpty();
        if (slot.required && !hasSig) { toast.error(`${slot.label} signature is required`); setBusy(false); return; }
        if (slot.required && slot.require_name !== false && !nm) { toast.error(`${slot.label} name is required`); setBusy(false); return; }
        const selfie = sigSelfies[i];
        if (slot.selfie_mode === 'required' && hasSig && !selfie) { toast.error(`${slot.label} selfie is required`); setBusy(false); return; }
        if (/client/i.test(slot.role)) clientNameOut = nm || clientNameOut;
        if (/tech|survey/i.test(slot.role)) surveyorNameOut = nm || surveyorNameOut;
        if (hasSig) {
          const r = await client.post('/signatures', { instruction_id: resolved.instruction_id, role: slot.role || `signer_${i}`, signer_name: nm || slot.label, image_data_url: url, selfie_data_url: slot.selfie_mode && slot.selfie_mode !== 'off' ? (selfie || null) : null, geofence });
          sigIds.push(r.data.id);
        }
      }
      // Submit
      const answersList = Object.entries(answers).map(([question_code, value]) => ({ question_code, value }));
      await axios.post(`${API_BASE}/public/surveys/${token}/submit`, { answers: answersList, geofence, geofence_bypass: geofenceBypass, photo_ids: photoIds, signature_ids: sigIds, surveyor_name: surveyorNameOut, client_name: clientNameOut });
      setStep('done'); toast.success('Survey submitted');
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Submit failed');
    } finally { setBusy(false); }
  };

  if (step === 'error') return (
    <div className="flex-1 flex items-center justify-center p-6">
      <Card className="max-w-md"><CardContent className="py-6 text-center"><div className="text-red-600 font-semibold mb-2">Cannot open survey</div><div className="text-sm text-raptor-navy/70">{errMsg}</div></CardContent></Card>
    </div>
  );

  if (!resolved) return <div className="flex-1 flex items-center justify-center">Loading survey…</div>;

  return (
    <div className="flex-1 max-w-4xl mx-auto w-full p-4 md:p-6 space-y-4">
      <div
        className="rounded-md p-5 md:p-6 text-white"
        style={{ background: 'linear-gradient(135deg, var(--raptor-navy, #061935) 0%, var(--raptor-blue, #0D63FF) 55%, var(--raptor-cyan, #36EBFE) 100%)' }}
      >
        <div className="flex items-center justify-between gap-3 mb-4">
          <RaptorLogo size={92} slot="survey_header_logo" markOnly showTagline={false} />
          <div className="flex flex-col items-center shrink-0">
            <img src="/brand/raptor-symbol-primary.png" alt="RAPTOR" className="h-16 w-auto object-contain" data-testid="survey-raptor-logo" />
            <span className="mt-1 text-[10px] uppercase tracking-[0.15em] text-white/70 font-body" data-testid="survey-raptor-tagline">Powered by RAPTOR</span>
          </div>
        </div>
        <div className="font-headline text-xl md:text-2xl tracking-wide">{resolved.template_name}</div>
        <div className="text-sm text-white/80 font-body mt-1">{resolved.client_name} · Ref {resolved.site_reference || '—'} · v{resolved.template_version} · {resolved.method === 'on_site' ? 'On-site survey' : 'Remote self-submission'}</div>
      </div>

      {step === 'geofence' && (
        <GeofenceGate
          mode={resolved.geo_mode || (resolved.geofence_required ? 'strict' : 'capture_only')}
          siteConfig={resolved.site_geofence || null}
          onCapture={onGeofenced}
          reverseGeocode={(lat, lng) =>
            axios.get<{ candidates: string[] }>(`${API_BASE}/public/settings/geocode/reverse`, { params: { lat, lng } })
              .then(r => r.data.candidates || []).catch(() => [])}
        />
      )}

      {(step === 'form' || step === 'review') && (() => {
        const sections = resolved.schema.sections;
        const totalSteps = sections.length + 1; // +1 for the review & sign step
        const currentStepNo = step === 'review' ? totalSteps : sectionIdx + 1;
        const progressPct = Math.round((currentStepNo / totalSteps) * 100);
        const stepLabel = step === 'review'
          ? 'Review & sign'
          : `Section ${sectionIdx + 1} of ${sections.length}: ${sections[sectionIdx]?.title ?? ''}`;
        return (
          <>
            <div className="space-y-1.5" data-testid="survey-progress">
              <div className="flex items-center justify-between text-xs font-body">
                <span className="font-semibold text-raptor-navy">{stepLabel}</span>
                <span className="text-raptor-navy/60 font-tabular">{progressPct}%</span>
              </div>
              <div className="flex items-start gap-1">
                {sections.map((sec, i) => {
                  const st = sectionStatus[i];
                  const active = step === 'form' && i === sectionIdx;
                  const left = st ? st.missing.length : 0;
                  const cls = active ? 'bg-raptor-blue' : st?.complete ? 'bg-raptor-blue/40' : 'bg-amber-500';
                  return (
                    <button key={i} type="button" data-testid={`survey-jump-${i}`} onClick={() => jumpTo(i)}
                      title={`${sec.title}${left > 0 ? ` — ${left} required left` : ' — complete'}`}
                      className="flex-1 flex flex-col items-center gap-1 group">
                      <span data-testid={`survey-seg-count-${i}`} className={`text-[10px] font-bold leading-none font-tabular ${left > 0 ? 'text-amber-600' : 'text-raptor-blue'}`}>{left > 0 ? left : '✓'}</span>
                      <span className={`w-full h-2 rounded-full transition-all group-hover:opacity-80 ${cls} ${active ? 'ring-2 ring-raptor-navy/30' : ''}`} />
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-raptor-navy/55 font-body">Tap a section to jump between them. Numbers show required answers still needed.</p>
            </div>

            {step === 'form' && (
              <>
                <SurveyForm schema={resolved.schema} activeSection={sectionIdx} answers={answers} onChange={(c, v) => setAnswers(a => ({ ...a, [c]: v }))} errors={errors} instructionId={resolved.instruction_id} token={token} lockedCodes={lockedCodes} />
                <div className="flex items-center justify-between gap-3 pt-2 pb-6">
                  <Button data-testid="survey-back" variant="outline" onClick={goBack} disabled={sectionIdx === 0} size="lg">
                    <ChevronLeft size={18} className="mr-1" /> Back
                  </Button>
                  <Button data-testid="survey-next" variant="gradient" onClick={goNext} size="lg">
                    {sectionIdx < sections.length - 1 ? 'Next' : 'Review & sign'} <ChevronRight size={18} className="ml-1" />
                  </Button>
                </div>
              </>
            )}

            {step === 'review' && (
              <>
                {reviewIncomplete.length > 0 ? (
                  <Card className="border-amber-300 border-l-4 border-l-amber-500" data-testid="submit-blockers">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle size={18} /> Before you submit — {reviewMissingCount} required answer{reviewMissingCount === 1 ? '' : 's'} left</CardTitle>
                      <CardDescription>Tap any item to jump straight to it.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {reviewIncomplete.map((sec) => (
                        <div key={sec.i} className="space-y-1">
                          <button type="button" data-testid={`blocker-section-${sec.i}`} onClick={() => jumpTo(sec.i)} className="flex items-center gap-1 text-sm font-semibold text-raptor-navy hover:text-raptor-blue transition-colors">
                            {sec.title} <ChevronRight size={14} />
                          </button>
                          <ul className="pl-3 space-y-1">
                            {sec.missing.map((m, j) => (
                              <li key={j}>
                                <button type="button" data-testid={`blocker-field-${sec.i}-${j}`} onClick={() => jumpTo(sec.i)} className="text-left text-sm text-raptor-navy/70 underline underline-offset-2 hover:text-raptor-blue transition-colors">• {m}</button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-l-4 border-l-green-500" data-testid="submit-ready">
                    <CardContent className="flex items-center gap-2 py-4 text-sm text-raptor-navy"><CheckCircle2 size={18} className="text-green-600" /> All required answers complete. You&apos;re ready to submit.</CardContent>
                  </Card>
                )}

                {!cameraOnlySurvey && (
                  <Card>
                    <CardHeader><CardTitle>Photo gallery</CardTitle><CardDescription>Optional captions per photo. Floor plan, plug, device placement, etc.</CardDescription></CardHeader>
                    <CardContent><PhotoGallery instructionId={resolved.instruction_id} token={token} /></CardContent>
                  </Card>
                )}

                {signatureSlots.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="flex items-center gap-2"><PenLine size={18} /> Signatures</CardTitle><CardDescription>Level 2 electronic signature (ECT Act §13). Audit certificate PDF generated automatically.</CardDescription></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      {signatureSlots.map((slot, i) => (
                        <div key={i} className="space-y-2" data-testid={`sig-block-${i}`}>
                          {slot.require_name !== false && (
                            <>
                              <Label>{slot.label} name{slot.required ? ' *' : ''}</Label>
                              <Input data-testid={`sig-name-${i}`} value={slotNameValue(i, slot)} onChange={(e) => setSigNames((n) => ({ ...n, [i]: e.target.value }))} />
                            </>
                          )}
                          <SignaturePad ref={(el) => { sigRefs.current[i] = el; }} label={`${slot.label} signature${slot.required ? ' *' : ''}`} />
                          {slot.selfie_mode && slot.selfie_mode !== 'off' && (
                            <div className="space-y-1" data-testid={`sig-selfie-block-${i}`}>
                              <Label>{slot.label} selfie{slot.selfie_mode === 'required' ? ' *' : ' (optional)'}</Label>
                              {sigSelfies[i] ? (
                                <div className="flex items-center gap-2">
                                  <img src={sigSelfies[i]} alt="selfie" className="h-16 w-16 rounded object-cover border border-border/40" />
                                  <button type="button" className="text-xs text-red-600 underline" onClick={() => setSigSelfies((m) => { const n = { ...m }; delete n[i]; return n; })} data-testid={`sig-selfie-clear-${i}`}>Remove</button>
                                </div>
                              ) : (
                                <input
                                  type="file"
                                  accept="image/*"
                                  capture="user"
                                  className="text-sm"
                                  data-testid={`sig-selfie-input-${i}`}
                                  onChange={(e) => onSelfiePick(i, e.target.files?.[0])}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-raptor-navy/60 font-body bg-raptor-neutral/40 rounded p-3">
                      <b>Signature capture on this device.</b> Alternatively, the client can sign later from their own phone via an SMS-link (issued from the insurer portal). Both modes create the same audit certificate.
                    </div>
                  </CardContent>
                </Card>
                )}

                <div className="flex items-center justify-between gap-3 pt-2 pb-6">
                  <Button data-testid="survey-back" variant="outline" onClick={goBack} size="lg">
                    <ChevronLeft size={18} className="mr-1" /> Back
                  </Button>
                  <Button data-testid="survey-submit" variant="gradient" onClick={submit} disabled={busy} size="lg">{busy ? 'Submitting…' : 'Submit survey'}</Button>
                </div>
              </>
            )}
          </>
        );
      })()}

      {step === 'done' && (
        <Card>
          <CardContent className="py-8 text-center">
            <CheckCircle2 size={40} className="mx-auto text-emerald-600 mb-2" />
            <div className="font-headline text-2xl text-raptor-navy">Survey submitted</div>
            <div className="text-sm text-raptor-navy/60 font-body mt-2">Thank you. The Osprey risk team has been notified.</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
